import { CheckCircle2, ArrowRight, ClipboardCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import StackUp from "@/components/StackUp";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { PCB_TYPES, SPEC_NOTE, DFM_CHECKS } from "@/data/site";

function SpecTable({ type }) {
    return (
        <FadeUp className="mt-8" >
            <div className="border border-slate-200" data-testid={`pcb-specs-${type.id}`}>
                <p className="border-b border-slate-200 bg-slate-50 px-5 py-3 font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">
                    Typical Specifications
                </p>
                <dl className="grid gap-px bg-slate-200 sm:grid-cols-2">
                    {type.specs.map((s) => (
                        <div key={s.k} className="bg-white px-5 py-3.5">
                            <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">{s.k}</dt>
                            <dd className="mt-1 text-[13.5px] font-medium text-navy">{s.v}</dd>
                        </div>
                    ))}
                </dl>
            </div>
            <p className="mt-3 font-mono text-[10.5px] leading-relaxed tracking-wide text-slate-400">
                * {SPEC_NOTE}
            </p>
        </FadeUp>
    );
}

export default function PCBManufacturing() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="PCB Manufacturing"
                eyebrow="Single · Double · Multilayer · Metal Core"
                titleLines={["PCB", "Manufacturing."]}
                lead="Detailed look at the PCB classes we manufacture — each built to customer design data, specification and quantity."
            />

            {/* Type quick nav */}
            <section className="border-b border-slate-200 bg-white py-0" data-testid="pcb-type-nav">
                <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-slate-200 lg:grid-cols-4">
                    {PCB_TYPES.map((t) => (
                        <a
                            key={t.id}
                            href={`#${t.id}`}
                            data-testid={`pcb-nav-${t.id}`}
                            className="group flex items-center justify-between bg-white px-5 py-5 transition-colors hover:bg-slate-50 sm:px-8"
                        >
                            <span className="flex items-center gap-3">
                                <span className="font-mono text-[11px] text-brand-red">{t.num}</span>
                                <span className="text-[13.5px] font-medium text-navy">{t.name}</span>
                            </span>
                            <ArrowRight className="h-4 w-4 text-slate-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-brand-red" />
                        </a>
                    ))}
                </div>
            </section>

            {PCB_TYPES.map((t, i) => (
                <section
                    key={t.id}
                    id={t.id}
                    className={`scroll-mt-24 py-20 md:py-28 ${i % 2 === 1 ? "border-y border-slate-200 bg-slate-50" : "bg-white"}`}
                    data-testid={`pcb-section-${t.id}`}
                >
                    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                        <FadeUp className={`relative ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                            <img src={t.image} alt={`${t.name} manufactured by Unimicron Technology India`} className="h-[320px] w-full object-cover md:h-[440px]" />
                            <div className={`absolute -bottom-4 hidden h-24 w-24 border-b-2 border-brand-red md:block ${i % 2 === 1 ? "-right-4 border-r-2" : "-left-4 border-l-2"}`} aria-hidden="true" />
                            <div className="absolute left-0 top-0 bg-navy-dark px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                                {t.tag}
                            </div>
                        </FadeUp>

                        <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                            <FadeUp>
                                <span className="font-mono text-[13px] tracking-[0.25em] text-brand-red">{t.num} /</span>
                                <h2 className="mt-4 text-3xl font-bold tracking-tight text-navy md:text-4xl">{t.name}</h2>
                                <p className="mt-6 text-[15px] leading-relaxed text-slate-600">{t.desc}</p>
                            </FadeUp>
                            <div className="mt-8 space-y-px bg-slate-200" data-testid={`pcb-features-${t.id}`}>
                                {t.features.map((f, j) => (
                                    <FadeUp key={f} delay={j * 0.05} className={`flex items-start gap-3 p-4 ${i % 2 === 1 ? "bg-slate-50" : "bg-white"}`}>
                                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                        <p className="text-[13.5px] leading-relaxed text-slate-700">{f}</p>
                                    </FadeUp>
                                ))}
                            </div>
                            <SpecTable type={t} />
                            <FadeUp className="mt-8">
                                <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-500">Typical Applications</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {t.applications.map((a) => (
                                        <span key={a} className="border border-slate-300 bg-white px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-slate-600">
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </FadeUp>
                            <FadeUp className="mt-10">
                                <button
                                    onClick={openQuote}
                                    data-testid={`pcb-quote-btn-${t.id}`}
                                    className="group inline-flex items-center gap-2 border border-brand-red bg-brand-red px-7 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                                >
                                    Get a Quote for {t.name}s
                                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                </button>
                            </FadeUp>
                        </div>
                    </div>
                </section>
            ))}

            {/* Stack-up explorer */}
            <section className="border-t border-slate-200 bg-white py-20 md:py-28" data-testid="pcb-stackup-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <div>
                            <Eyebrow>Inside a Multilayer Board</Eyebrow>
                            <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                How the layers bond together
                            </h2>
                        </div>
                        <p className="max-w-md text-[14px] leading-relaxed text-slate-500">
                            Multilayer PCBs are built from copper and dielectric layers laminated into
                            a single board. Explore a typical 4-layer cross-section.
                        </p>
                    </FadeUp>
                    <FadeUp className="mt-12">
                        <StackUp />
                    </FadeUp>
                </div>
            </section>

            {/* DFM review */}
            <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="pcb-dfm">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                    <div className="lg:col-span-2">
                        <FadeUp>
                            <Eyebrow>DFM Review</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Checked before your boards enter production
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                Every Gerber package passes a manufacturability review before quoting
                                and production. If something in the data will cause problems on the
                                floor, we catch it — and tell you — before the build, not after.
                            </p>
                            <button
                                onClick={openQuote}
                                data-testid="dfm-quote-btn"
                                className="mt-8 border border-brand-red bg-brand-red px-7 py-3.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                            >
                                Send Gerber for Review
                            </button>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2" data-testid="pcb-dfm-checks">
                            {DFM_CHECKS.map((c, i) => (
                                <FadeUp key={c} delay={i * 0.05} className="bg-white p-6">
                                    <ClipboardCheck className="h-5 w-5 text-brand-red" strokeWidth={1.5} />
                                    <p className="mt-3.5 text-[13.5px] leading-relaxed text-slate-700">{c}</p>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-navy-dark py-20 text-white md:py-24" data-testid="pcb-cta">
                <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
                    <div>
                        <h2 className="max-w-2xl text-3xl font-bold tracking-tight md:text-4xl">
                            Not sure which PCB type fits your design?
                        </h2>
                        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
                            Send us your Gerber data and application details — our team will advise
                            the right board class and respond with pricing.
                        </p>
                    </div>
                    <button
                        onClick={openQuote}
                        data-testid="pcb-cta-btn"
                        className="inline-flex shrink-0 items-center gap-2 border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                    >
                        Send Gerber Data <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </section>
        </>
    );
}
